import React, { useMemo } from 'react';
import { AppTable, Dropdown, InputNumber, StatusTag, Tooltip } from '@allygpo/ally-ux';
import { getCoreRowModel, useReactTable, type ColumnDef, type Table } from '@tanstack/react-table';
import { alignEnd, headerEnd, tableView, unpaginated } from '../../components/ui';

type Option = { label: string; value: string };

export interface AlternateAssignmentRow {
  payer: string;
  /** Share of the area, already formatted. */
  share: string;
  selectedProductFamilyId: string;
}

/** What the alternate-assignment cells read from `meta.view`. */
interface AssignmentView {
  /** The product families this payer may be assigned to. */
  optionsFor: (payer: string) => Option[];
  onAssign: (payer: string, productFamilyId: string) => void;
  disabled: boolean;
}

const assignmentColumns: ColumnDef<AlternateAssignmentRow>[] = [
  { id: 'payer', size: 300, header: 'Payer', cell: ({ row: { original: row } }) => <span className="pa-name font-medium">{row.payer}</span> },
  { id: 'share', size: 90, header: () => headerEnd('Share'), meta: alignEnd, cell: ({ row: { original: row } }) => row.share },
  {
    id: 'assign',
    size: 260,
    header: 'Assign to product family',
    cell: ({ row: { original: row }, table }) => {
      const { optionsFor, onAssign, disabled } = tableView<AssignmentView, AlternateAssignmentRow>(table);
      const options = optionsFor(row.payer);
      // With 0 or 1 eligible families there's nothing to choose.
      if (options.length === 0) return <span className="siq-dim text-tertiary">—</span>;
      if (options.length === 1) return options[0].label;
      return (
        <Dropdown
          value={row.selectedProductFamilyId || null}
          options={options}
          optionLabel="label"
          optionValue="value"
          placeholder="Select"
          onChange={(e) => onAssign(row.payer, String(e.value))}
          className="w-full"
          disabled={disabled}
          aria-label={`Product family for ${row.payer}`}
        />
      );
    },
  },
];

/** The alternate scenario's editor: reassign each payer to an eligible product family. */
export const AlternateAssignmentTable: React.FC<{ rows: AlternateAssignmentRow[] } & AssignmentView> = ({ rows, optionsFor, onAssign, disabled }) => {
  const table = useReactTable({
    ...unpaginated,
    data: rows,
    columns: assignmentColumns,
    meta: { view: { optionsFor, onAssign, disabled } satisfies AssignmentView },
    getRowId: (row) => row.payer,
    getCoreRowModel: getCoreRowModel(),
  });
  return <AppTable isBordered={false} size="sm" className="siq-dense-inputs" ariaLabel="Alternate payer assignments" table={table} />;
};

export interface AlternatePreviewRow {
  productFamilyId: string;
  productFamilyName: string;
  currentDistribution: number;
  alternateDistribution: number;
}

/** What the preview cells read from `meta.view`. */
interface PreviewView {
  scenarioLabel: string;
  renderFamily: (name: string) => React.ReactNode;
  renderShare: (fraction: number) => React.ReactNode;
}

const previewColumns: ColumnDef<AlternatePreviewRow>[] = [
  {
    id: 'family',
    size: 340,
    header: 'Product family',
    cell: ({ row: { original: row }, table }) => tableView<PreviewView, AlternatePreviewRow>(table).renderFamily(row.productFamilyName),
  },
  {
    id: 'current',
    size: 155,
    header: ({ table }) => tableView<PreviewView, AlternatePreviewRow>(table).scenarioLabel,
    cell: ({ row: { original: row }, table }) => tableView<PreviewView, AlternatePreviewRow>(table).renderShare(row.currentDistribution),
  },
  {
    id: 'alternate',
    size: 155,
    header: 'Alternate',
    cell: ({ row: { original: row }, table }) => tableView<PreviewView, AlternatePreviewRow>(table).renderShare(row.alternateDistribution),
  },
];

/** Each product family's share in the scenario next to the alternate's. */
export const AlternatePreviewTable: React.FC<{ rows: AlternatePreviewRow[] } & PreviewView> = ({ rows, scenarioLabel, renderFamily, renderShare }) => {
  const table = useReactTable({
    ...unpaginated,
    data: rows,
    columns: previewColumns,
    meta: { view: { scenarioLabel, renderFamily, renderShare } satisfies PreviewView },
    getRowId: (row) => row.productFamilyId,
    getCoreRowModel: getCoreRowModel(),
  });
  return <AppTable isBordered={false} size="sm" ariaLabel={`${scenarioLabel} vs Alternate`} table={table} />;
};

export interface AlternateMixRow {
  productFamilyId: string;
  productFamilyName: string;
  /** The base scenario's share, 0–1. */
  currentDistribution: number;
}

/** A product family, or the Total row that closes the table. */
type MixRow = ({ kind: 'family' } & AlternateMixRow) | { kind: 'total' };

/** What the alternate-mix cells read from `meta.view`. */
interface MixView {
  baseLabel: string;
  /** Alternate share per product family id, 0–1. */
  values: Record<string, number>;
  onChange: (productFamilyId: string, fraction: number) => void;
  /** Max eligible share (whole %) per product family id, when payer data is known. */
  maxShare: Record<string, number | undefined>;
  labelFor: (name: string) => string;
  colorFor: (name: string) => string;
  renderShare: (fraction: number, color: string) => React.ReactNode;
  /** Total of the base scenario, whole %. */
  currentTotalPct: number;
  /** Total of the alternate, whole %. */
  totalPct: number;
}

const mixView = (table: Table<MixRow>) => tableView<MixView, MixRow>(table);
const altPctOf = (view: MixView, id: string) => Math.round((view.values[id] ?? 0) * 100);

const mixColumns: ColumnDef<MixRow>[] = [
  {
    id: 'family',
    size: 200,
    header: 'Product family',
    cell: ({ row: { original: row }, table }) => {
      if (row.kind === 'total') return 'Total';
      const view = mixView(table);
      const maxPct = view.maxShare[row.productFamilyId];
      const exceedsMax = maxPct !== undefined && altPctOf(view, row.productFamilyId) > maxPct;
      const warnClass = `alt-warn-${row.productFamilyId}`;
      return (
        <span className="inline-flex align-items-center u-gap-2 font-medium">
          <i className="siq-swatch" style={{ background: view.colorFor(row.productFamilyName) }} />
          <span>{view.labelFor(row.productFamilyName)}</span>
          {exceedsMax && (
            <>
              <Tooltip target={`.${warnClass}`} position="top" />
              <i className={`pi pi-exclamation-triangle siq-warn-icon text-warning text-xs ${warnClass}`} data-pr-tooltip={`Exceeds max eligible share of ${maxPct}% based on payer preferences`} />
            </>
          )}
        </span>
      );
    },
  },
  {
    id: 'current',
    size: 150,
    header: ({ table }) => `${mixView(table).baseLabel} mix`,
    cell: ({ row: { original: row }, table }) => {
      const view = mixView(table);
      return row.kind === 'total' ? `${view.currentTotalPct}%` : view.renderShare(row.currentDistribution, view.colorFor(row.productFamilyName));
    },
  },
  {
    id: 'alternate',
    size: 120,
    header: 'Alternate mix',
    cell: ({ row: { original: row }, table }) => {
      const view = mixView(table);
      if (row.kind === 'total') {
        return <span className="flex justify-content-end"><StatusTag variant={view.totalPct === 100 ? 'success' : 'danger'}>{view.totalPct}%</StatusTag></span>;
      }
      return (
        <InputNumber
          value={altPctOf(view, row.productFamilyId)}
          onValueChange={(e) => {
            const pct = typeof e.value === 'number' ? e.value : 0;
            view.onChange(row.productFamilyId, Math.max(0, Math.min(100, Math.round(pct))) / 100);
          }}
          min={0}
          max={100}
          step={1}
          suffix="%"
          useGrouping={false}
          minFractionDigits={0}
          maxFractionDigits={0}
          className="w-full"
          inputClassName="w-full text-right"
          placeholder="0 %"
          aria-label={`Alternate mix for ${view.labelFor(row.productFamilyName)}`}
        />
      );
    },
  },
  {
    id: 'change',
    size: 90,
    header: () => headerEnd('Change'),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => {
      const view = mixView(table);
      if (row.kind === 'total') {
        const { totalPct } = view;
        const valid = totalPct === 100;
        return <span className={`text-2xs font-medium ${valid ? 'siq-pos text-success' : 'text-danger'}`}>{valid ? 'Ready' : totalPct < 100 ? `${100 - totalPct}% remaining` : `${totalPct - 100}% over`}</span>;
      }
      const change = altPctOf(view, row.productFamilyId) - Math.round(row.currentDistribution * 100);
      return change === 0 ? <span className="siq-dim text-tertiary">—</span> : `${change > 0 ? '+' : '−'}${Math.abs(change)} pts`;
    },
  },
];

/** The alternate scenario's mix editor (distribution strategies): a share per product family, then the total. */
export const AlternateMixTable: React.FC<{ rows: AlternateMixRow[] } & MixView> = ({ rows, ...view }) => {
  const data = useMemo<MixRow[]>(() => [...rows.map((row) => ({ kind: 'family' as const, ...row })), { kind: 'total' }], [rows]);
  const table = useReactTable({
    ...unpaginated,
    data,
    columns: mixColumns,
    meta: { view },
    getRowId: (row) => (row.kind === 'total' ? '__total' : row.productFamilyId),
    getCoreRowModel: getCoreRowModel(),
  });
  return <AppTable isBordered={false} size="sm" className="siq-dense-inputs" ariaLabel="Alternate mix" table={table} rowClassName={({ rowData }) => (rowData.kind === 'total' ? 'is-total' : '')} />;
};
